// useAudioPrefetch — background-fetch audio so the Service Worker
// can cache it via the workbox StaleWhileRevalidate rule on /audio/.
//
// Day 14b lesson learned: do NOT use mode:"no-cors". Opaque responses
// got cached, then iOS Range requests couldn't slice them. Now we use
// mode:"cors" — Firebase Storage downloadURL returns
// Access-Control-Allow-Origin:*, so the browser gets a real 200
// response that's fully readable + range-sliceable.
//
// We don't decode or store anything ourselves — the fetch is enough for
// the SW route's cacheWillUpdate (which strictly accepts only 200s) to
// capture the response in gb-audio. The Playlist label uses the
// returned `downloaded` Set to show "已下載" / "連線時下載".
//
// Concurrency: 2. Audio files are ~1-2MB each; 15 files × 2 concurrent
// = ~30-60MB peak network burst during prefetch. 2.5s after sync wakes,
// throttled in PrefetchKickoff.

import { type AudioDoc, storageUrl, type WithId } from "@goodbye/firebase";
import { useEffect, useState } from "react";

const CACHE_NAME = "gb-audio";
const MAX_CONCURRENT = 2;

export function useAudioPrefetch(
  audios: WithId<AudioDoc>[],
): Set<string> {
  const [downloaded, setDownloaded] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    // Prefetch ALL uploaded storagePaths, not just released ones.
    // Learners walking into hill-side dead zones need every audio cached
    // before signal drops. Release status only gates UI visibility — once
    // uploaded, the file is on the device.
    const playable = audios.filter((a) => a.storagePath);

    async function syncCacheState() {
      if (typeof caches === "undefined") return;
      try {
        const cache = await caches.open(CACHE_NAME);
        const hits = new Set<string>();
        for (const a of playable) {
          const url = await storageUrl(a.storagePath).catch(() => "");
          if (!url) continue;
          const match = await cache.match(url);
          if (match) hits.add(a.id);
        }
        if (!cancelled) setDownloaded(hits);
      } catch {
        /* SW or caches unavailable — silent */
      }
    }

    async function prefetchAll() {
      // Concurrency-limited queue.
      const queue = [...playable];
      async function worker() {
        while (!cancelled && queue.length > 0) {
          const a = queue.shift();
          if (!a) return;
          const url = await storageUrl(a.storagePath).catch(() => "");
          if (!url) continue;
          try {
            // mode:"cors" so we get a real 200 response that the SW
            // strict cacheWillUpdate will accept. Firebase Storage
            // downloadURL allows CORS (Access-Control-Allow-Origin:*).
            // cache:"no-store" tells the browser HTTP cache not to
            // shortcut — we want the SW to see and capture the fetch.
            await fetch(url, { mode: "cors", cache: "no-store" });
          } catch {
            /* offline / blocked — next mount retries */
          }
        }
      }
      await Promise.all(
        Array.from({ length: MAX_CONCURRENT }, () => worker()),
      );
      await syncCacheState();
    }

    syncCacheState();
    // Microtask delay so we don't block first paint.
    const t = window.setTimeout(prefetchAll, 1500);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [audios]);

  return downloaded;
}
